import { useTranslate } from '@tolgee/react';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import FoldedHandsSVG from '../../assets/icons/FoldedHands';
import useGame from '../../hooks/useGame';
import { LessonMediaPreFetcher } from '../../hooks/useMediaPreFetcher';
import LiveActionsDisabledProvider from '../../providers/LiveActionsDisabledProvider';
import { Game } from '../../services/backendService/types';
import LiveEmptyState from '../_molecules/LiveEmptyState';
import { findRelatedSlidesWithoutIndex } from '../SlideComponents/slideUtil';
import { CSSSlideWrapperViewer } from '../SlideComponents/SlideWrapperViewer';
import LiveGameActionViewerButtons from './LiveGameViewerButtons';

const LiveGameViewer = () => {
  const router = useRouter();
  const { update } = useIntercom();
  const { gameId, tenantId } = router.query as { gameId: string; tenantId: string };
  const { t } = useTranslate();
  const [showActivityMedia, setShowActivityMeida] = useState(false);
  const { game } = useGame(gameId);
  const [localGame, setLocalGame] = useState<Game | undefined>();

  useEffect(() => {
    update({
      hideDefaultLauncher: true,
    });
    return () => {
      update({
        hideDefaultLauncher: false,
      });
    };
  }, [update]);

  useEffect(() => {
    if (game && !localGame && !!game.slides) {
      const slides = game.slides;
      const playedSlides = slides.filter(r => {
        if (r.type === 'slide') return true;
        const relatedSlides = findRelatedSlidesWithoutIndex(r.id, slides);
        if (relatedSlides.find(r => !r.wasPlayed)) return false;
        return true;
      });
      let initialCurrentRoundId;
      if (playedSlides.length > 0) {
        initialCurrentRoundId = playedSlides.find(
          r =>
            r.type === 'podium' ||
            r.type === 'responsegrid' ||
            r.type === 'slide' ||
            r.type === 'wordcloud' ||
            r.type === 'poll',
        )?.id;

        // set query like ...eBihTy/live?roundId=123123123 to go directly into the round with id 123123123
        const queryPath = router.asPath.split('?');
        if (queryPath.length > 1) {
          try {
            const queryRoundId = queryPath[1].split('=')[1];

            if (playedSlides?.find(r => r.id === queryRoundId)) {
              initialCurrentRoundId = queryRoundId;
            }
          } catch (error) {}
        }
      }
      setLocalGame({
        ...game,
        currentRound: initialCurrentRoundId ? initialCurrentRoundId : null,
        slides: playedSlides ? playedSlides : [],
      });
    }
  }, [game, localGame, router.asPath]);
  const currentSlide = localGame?.slides?.find(r => r.id === localGame?.currentRound);
  const backgroundMedia = useMemo(() => {
    if (!currentSlide) return undefined;
    if (!game?.slides) return undefined;
    const relatedSlides = findRelatedSlidesWithoutIndex(currentSlide.id, game.slides);
    return relatedSlides[0]?.backgroundMedia;
  }, [currentSlide, game?.slides]);
  return (
    <LiveActionsDisabledProvider>
      {game?.slides && (
        <LessonMediaPreFetcher
          slides={game?.slides}
          isAuthenticated={false}
          isDiscoveredTemplate={false}
          isOwner={false}
        />
      )}

      <GameContainer>
        {localGame?.slides && localGame.slides.length === 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '20px',
              height: '100%',
              zIndex: 10,
            }}
          >
            <LiveEmptyState
              title={t('This game has no completed rounds')}
              description=""
              icon={<FoldedHandsSVG />}
              style={{ zIndex: 1 }}
            />
          </div>
        )}
        {currentSlide && localGame && (
          <CSSSlideWrapperViewer
            aspect={{ height: 1080, width: 1920 }}
            slide={currentSlide}
            backgroundMedia={backgroundMedia}
            resultView={!showActivityMedia}
            showStaticPoll={false}
            hideLogo={false}
            gameId={localGame.id}
            tenantId={tenantId}
            isAuthenticated={false}
            slides={localGame.slides || []}
            isDiscoverTemplate={localGame.isFromDiscover || false}
            isUsingSockets={false}
            lessonAttachments={game?.attachments}
          >
            <LiveGameActionViewerButtons
              localGame={localGame}
              slides={localGame.slides || []}
              setLocalGame={setLocalGame}
              currentSlide={currentSlide.id}
              setShowMedia={setShowActivityMeida}
              showActivityMedia={showActivityMedia}
            />
          </CSSSlideWrapperViewer>
        )}
      </GameContainer>
    </LiveActionsDisabledProvider>
  );
};

export default LiveGameViewer;

const GameContainer = styled.div`
  height: 100%;
  width: 100%;
  /* Reserve scrollbar gutter so its appearance/disappearance can't shrink the
     slide's available width and trigger a ResizeObserver feedback loop that
     leaves the slide rendered smaller than the viewport. */
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  position: relative;
  background-color: black;
`;
